import React from 'react';
import { LuUsers, LuBed, LuMaximize, LuLeaf } from 'react-icons/lu';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { PageWrapper } from '../components/PageWrapper';

const mockRooms = {
  'chembra-view-suite': { _id:'1', slug:'chembra-view-suite', name:'Chembra View Suite', type:'suite', pricePerNight:6500, capacity:2, bedType:'King Bed', size:420, description:'The jewel of Villa Tesori — a luxurious suite perched at the highest point of the property with unobstructed, panoramic views of the majestic Chembra Peak. Wake up each morning to the peak emerging from the mist, sip your tea on the private balcony as the valley comes to life below. Fitted with a king bed, elegant wooden furnishings, and an en-suite bathroom with premium amenities, this is the ultimate Wayanad experience.', shortDesc:'Wake up to the majestic Chembra Peak from your private balcony.', view:'Mountain', amenities:['360° Mountain View','Private Balcony','King Bed','Air Conditioning','High-Speed WiFi','En-suite Bathroom','Hot Water','Premium Toiletries','Writing Desk','Room Service','Mini Fridge'], images:[{ url:'https://images.unsplash.com/photo-1611892440504-42a792e24d32?w=1200', alt:'Chembra View Suite' },{ url:'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=1200', alt:'Suite interior' }] },
};

export default function RoomDetail() {
  const { slug } = useParams();

  const { data: room, isLoading } = useQuery({
    queryKey: ['room', slug],
    queryFn: () => axios.get(`/api/rooms/${slug}`).then(r => r.data).catch(() => mockRooms[slug] || mockRooms['chembra-view-suite'])
  });

  if (isLoading) return <div className="flex-center" style={{ height: '100vh' }}><div className="loading-spinner" /></div>;
  if (!room) return <div style={{ padding: 80, textAlign: 'center' }}>Room not found</div>;

  return (
    <PageWrapper>
      <Helmet>
        <title>{room.name} – Villa Tesori Wayanad | Kerala Luxury Homestay</title>
        <meta name="description" content={`${room.shortDesc} Book the ${room.name} at Villa Tesori, Vellarimala, Wayanad. Starting from ₹${room.pricePerNight?.toLocaleString()}/night.`} />
        <meta property="og:title" content={`${room.name} – Villa Tesori`} />
        <meta property="og:image" content={room.images?.[0]?.url} />
      </Helmet>

      {/* Hero image */}
      <div className="room-detail-hero" style={{ height: 'clamp(260px, 50vw, 580px)', position: 'relative', overflow: 'hidden', marginTop: 70 }}>
        <img src={room.images?.[0]?.url} alt={room.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(26,46,26,0.6) 0%, transparent 50%)' }} />
        <div className="hero-text" style={{ position: 'absolute', bottom: 'clamp(16px,4vw,40px)', left: 'clamp(16px,5vw,60px)' }}>
          <span className="badge badge-gold" style={{ textTransform: 'capitalize', marginBottom: 12, display: 'inline-block' }}>{room.type}</span>
          <h1 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(1.8rem, 4vw, 3.5rem)', color: 'var(--white)', fontWeight: 300 }}>{room.name}</h1>
        </div>
      </div>

      <section className="section">
        <div className="container">
          <div className="room-detail-grid" style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 80 }}>
            {/* Details */}
            <div>
              {/* Quick specs */}
              <div className="room-specs-row" style={{ display: 'flex', gap: 32, marginBottom: 32, padding: '24px 0', borderBottom: '1px solid var(--mist)', borderTop: '1px solid var(--mist)', flexWrap: 'wrap' }}>
                {[
                  { icon: <LuUsers />, label: 'Capacity', val: `${room.capacity} Guests` },
                  { icon: <LuBed />, label: 'Bed', val: room.bedType },
                  { icon: <LuMaximize />, label: 'Size', val: room.size ? `${room.size} sq ft` : 'Spacious' },
                  { icon: <LuLeaf />, label: 'View', val: room.view || 'Natural' },
                ].map(s => s.val && (
                  <div key={s.label} style={{ textAlign: 'center', minWidth: 60 }}>
                    <div style={{ fontSize: '1.4rem', marginBottom: 8, color: 'var(--gold)' }}>{s.icon}</div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>{s.label}</div>
                    <div style={{ fontSize: '0.9rem', color: 'var(--text-body)', marginTop: 2 }}>{s.val}</div>
                  </div>
                ))}
              </div>

              <div className="section-label">About This Room</div>
              <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(1.6rem, 3vw, 2rem)', color: 'var(--forest)', marginBottom: 16 }}>{room.name}</h2>
              <p style={{ lineHeight: 1.9, color: 'var(--text-body)', marginBottom: 32, fontSize: '1rem' }}>{room.description}</p>

              <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.5rem', marginBottom: 20 }}>Room Amenities</h3>
              <div className="room-amenity-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 40 }}>
                {room.amenities?.map(a => (
                  <div key={a} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--cream)', borderRadius: 2 }}>
                    <span style={{ color: 'var(--gold)', fontSize: '0.9rem' }}>✓</span>
                    <span style={{ fontSize: '0.88rem', color: 'var(--text-body)' }}>{a}</span>
                  </div>
                ))}
              </div>

              {room.images?.length > 1 && (
                <>
                  <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.5rem', marginBottom: 20 }}>Photo Gallery</h3>
                  <div className="room-photo-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                    {room.images.slice(1).map((img, i) => (
                      <div key={i} className="img-overlay" style={{ height: 200, borderRadius: 2 }}>
                        <img src={img.url} alt={img.alt || room.name} />
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Booking panel */}
            <div>
              <div className="room-booking-sticky" style={{ position: 'sticky', top: 100, background: 'var(--white)', border: '1px solid var(--mist)', borderRadius: 2, overflow: 'hidden' }}>
                <div style={{ background: 'var(--forest)', padding: '28px clamp(20px,4vw,32px)' }}>
                  <div style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.18em', color: 'rgba(200,217,192,0.7)', marginBottom: 6 }}>Starting from</div>
                  <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2rem,5vw,2.8rem)', color: 'var(--gold-light)' }}>
                    ₹{room.pricePerNight?.toLocaleString()}
                    <span style={{ fontFamily: 'Jost, sans-serif', fontWeight: 300, fontSize: '1rem', color: 'rgba(200,217,192,0.6)' }}>/night</span>
                  </div>
                  <div style={{ marginTop: 8, fontSize: '0.8rem', color: 'rgba(200,217,192,0.6)' }}>Includes breakfast · Taxes extra</div>
                </div>
                <div style={{ padding: '28px clamp(20px,4vw,32px)' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
                    {[
                      ['Check-in', '2:00 PM'],
                      ['Check-out', '11:00 AM'],
                      ['Min. Stay', '1 Night'],
                      ['Cancellation', 'Refer to cancellation policy'],
                    ].map(([k, v]) => (
                      <div key={k} style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: 12, borderBottom: '1px solid var(--mist)' }}>
                        <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>{k}</span>
                        <span style={{ fontSize: '0.82rem', color: 'var(--text-body)', fontWeight: 500 }}>{v}</span>
                      </div>
                    ))}
                  </div>
                  <Link to={`/contact?room=${encodeURIComponent(room.name)}`} className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', marginBottom: 12 }}>
                    Request to Book
                  </Link>
                  <a href="https://wa.me/917558806677" target="_blank" rel="noopener noreferrer" className="btn btn-outline" style={{ width: '100%', justifyContent: 'center', textAlign: 'center' }}>
                    WhatsApp to Book
                  </a>
                  <p style={{ textAlign: 'center', fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 16 }}>
                    We respond within 24 hours
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-sm" style={{ background: 'var(--cream)', textAlign: 'center' }}>
        <div className="container-sm">
          <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.8rem', marginBottom: 8 }}>Explore More Rooms</h3>
          <p style={{ color: 'var(--text-muted)', marginBottom: 24 }}>Find the perfect space for your Wayanad retreat</p>
          <Link to="/rooms" className="btn btn-outline">View All Rooms</Link>
        </div>
      </section>
    </PageWrapper>
  );
}
